// 화면관리(SY13) 뷰 — 디자인 핸드오프 없이 콤보관리(sy12) 뷰(`comboManagement/ComboManagementView.tsx`)를 복사해 고친 손코드(LOGISPRD-16043).
//  같은 core.* 요소·화면 순서(제목 → 조회 카드 → 표 묶음)를 쓰고, 표를 HEAD → ITEM·SUB 3개로 늘렸다(1.0 SY03 배치: 위 HEAD, 아래 ITEM·SUB 나란히).
//  위 표 행을 더블클릭하면 아래 표를 조회한다(1.0 SY03 rowDoubleClicked). 엑셀·필터 칸·드래그 순서는 뺐다(티켓 범위).
//  배선(Wiring)은 화면 엔트리(index.tsx)가 props 로 넘긴다.
import { core } from '@musinsa/pbo-core'
import { pboLogisticsTheme } from '@musinsa/pbo-core/theme'
import '@musinsa/pbo-core/tokens.css'
import '@musinsa/pbo-core/styles.css'
import '@musinsa/pbo-core/fonts.css'
import './screenShell.css'
import { type ComponentProps, type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { Icon as DsIcon } from '@musinsa/pbo-assets'
import { usePageHeaderSticky } from '@/components/pbo/pageHeaderSticky'
import type { usePermissionSlot } from '@/hooks/useMenuPermission'
import type { ButtonCode } from '@/services/auth/menu.types'
import { HEAD, ITEM, SUB, SY13_PAGE_SIZE, TABLE_IDS, TABLE_NOUN, type TableId } from './consts'

// 모듈 홀더: 확인창 함수가 같은 배선을 보도록 렌더 첫머리에서 갱신한다.
let wiring: Wiring = { rows: {}, actions: {} } as Wiring

export type Row = Record<string, unknown> & { key: string; __new?: boolean }
export type Option = { value: string; label: string }
/** 액션 슬롯 인자 — selected = 그 표의 체크 선택 행 */
export type ActionCtx = { selected: Row[] }
export type ColumnLayout = Pick<core.CoreTableProps, 'columnOrder' | 'onColumnOrderChange' | 'columnWidths' | 'onColumnWidthsChange' | 'pinnedColumns' | 'onPinnedColumnsChange' | 'hiddenColumns' | 'onHiddenColumnsChange'>
export type Wiring = {
  /** 표 데이터 — 클라이언트 페이징. loading 이면 표 위에 로더를 띄운다. */
  rows: Record<TableId, () => { rows: Row[]; loading?: boolean }>
  /** 위 표(HEAD·ITEM)에서 더블클릭한 행 key — 하이라이트. caption = 바로 아래 표 제목 옆에 붙는 조회 기준(예: 'MUSINSA · GI38') */
  picked: Partial<Record<TableId, { key: string; caption: string }>>
  /** 셀 선택지 — HEAD 버튼(MenuAction 코드) · SUB 칼럼코드(같은 GRID_CD 의 기존 COLUMN_CD) */
  options: { buttons: Option[]; columnCodes: Option[] }
  /** 동작 — 실패는 throw(확인창이 열린 채 남고 선택도 유지된다). 성공 토스트·닫기는 뷰가 한다. */
  actions: {
    search: (filters: Record<string, unknown>) => void | false | Promise<void | false>
    /** 위 표 행 더블클릭 — HEAD 면 ITEM, ITEM 이면 SUB 를 조회한다 */
    pick: (table: typeof HEAD | typeof ITEM, row: Row) => void
    add: (table: TableId) => void
    rowsChange: (table: TableId, rows: Row[]) => void
    save: (table: TableId, ctx: ActionCtx) => void | Promise<void>
    remove: (table: TableId, ctx: ActionCtx) => void | Promise<void>
  }
  /** [추가] 가능 여부 — 아래 표는 위 행을 고른 뒤에만 */
  canAdd: (table: TableId) => boolean
  /** [삭제] 확인창에 덧붙일 문구 — HEAD 는 함께 지워지는 ITEM·SUB 건수. 실패는 throw(확인창을 열지 않는다) */
  deleteNotice?: (table: TableId, ctx: ActionCtx) => ReactNode | Promise<ReactNode>
  /** 요청 진행 중 여부(React 훅) — true 면 선택 게이트 버튼(저장·삭제)이 잠긴다. */
  useBusy?: () => boolean
  /** [초기화] 때 — 조회 결과도 지운다. 뷰는 조회영역 값·페이저·선택·목록 검색어를 되돌린다. */
  onReset?: () => void
  columnLayout?: Partial<Record<TableId, (columns: core.CoreColumnType[]) => ColumnLayout>>
  /** 버튼 권한·화면관리 그리드 설정(usePermissionSlot 값). 없으면 제한하지 않는다. */
  permission?: ReturnType<typeof usePermissionSlot>
}

const permCan = (code: ButtonCode) => wiring.permission?.canShow(code) ?? true
const permAdd = (table: TableId) => wiring.permission?.forTable(table).toolbar.add ?? true
const permCols = (table: TableId, columns: core.CoreColumnType[]) => wiring.permission?.forTable(table).columns(columns) ?? columns
const editable = () => wiring.permission?.editable ?? true

const textOf = (v: unknown) => (v == null ? '' : String(v))

// 편집한 행을 그 표 선택에 더하는 함수 — 표 구획이 마운트 동안 꽂는다. render 로 그린 Select 셀도 이 길로 바꾼다
const markEdited: Partial<Record<TableId, (rows: Row[]) => void>> = {}
const cellChange = (table: TableId, row: Row) => {
  markEdited[table]?.([row])
  wiring.actions.rowsChange(table, [row])
}

/** HEAD 버튼 칸 — 버튼 코드 여러 개(배열). 잠금이면 글자로 보인다 */
const buttonsCell = (value: unknown, row: Row) => {
  const codes = Array.isArray(value) ? (value as string[]) : []
  if (!editable()) return codes.join(', ')
  return (
    <core.Select
      mode="multiple"
      size="small"
      variant="borderless"
      style={{ width: '100%' }}
      options={wiring.options.buttons}
      value={codes}
      onChange={(next: string[]) => cellChange(HEAD, { ...row, BUTTONS: next })}
    />
  )
}

/** SUB 칼럼코드 칸 — [추가]한 새 행만 목록에서 고른다. 기존 행은 잠근다(티켓 결정: 추가 때만 입력) */
const columnCodeCell = (value: unknown, row: Row) => {
  if (!row.__new || !editable()) return textOf(value)
  return (
    <core.Select
      showSearch
      size="small"
      variant="borderless"
      placeholder="입력"
      status={value ? undefined : 'error'}
      style={{ width: '100%' }}
      options={wiring.options.columnCodes}
      value={value ? String(value) : undefined}
      onChange={(next: string) => cellChange(SUB, { ...row, COLUMN_CD: next })}
    />
  )
}

/** 컬럼 키·제목·별표 = 1.0 SY03_HEAD/ITEM/SUB 메타(dev MOMS_GRID_COLUMN_INFO, 2026-11-05 조회). 칸은 티켓 「대상」 표의 칸만 */
const COLUMNS: Record<TableId, core.CoreColumnType[]> = {
  [HEAD]: [
    { title: '화주코드', width: 140, variant: 'input', required: true, dataIndex: 'SPR_NM' },
    { title: '메뉴코드', width: 120, variant: 'input', required: true, dataIndex: 'MENU_CD' },
    { title: '메뉴명', width: 200, variant: 'input', required: true, dataIndex: 'MENU_NM' },
    { title: '버튼', width: 360, dataIndex: 'BUTTONS', render: (v: unknown, r) => buttonsCell(v, r as Row) },
  ],
  [ITEM]: [
    { title: '그리드코드', width: 160, variant: 'input', required: true, dataIndex: 'GRID_CD' },
    { title: '로우추가', width: 80, variant: 'checkbox', dataIndex: 'TOOL_ADD' },
    { title: '로우삭제', width: 80, variant: 'checkbox', dataIndex: 'TOOL_RMV' },
    { title: '엑셀저장', width: 80, variant: 'checkbox', dataIndex: 'TOOL_EXP' },
    { title: '엑셀열기', width: 80, variant: 'checkbox', dataIndex: 'TOOL_IMP' },
  ],
  [SUB]: [
    { title: '칼럼코드', width: 180, required: true, dataIndex: 'COLUMN_CD', render: (v: unknown, r) => columnCodeCell(v, r as Row) },
    { title: '필수입력', width: 80, variant: 'checkbox', dataIndex: 'COLUMN_REQ' },
    { title: '일괄적용', width: 80, variant: 'checkbox', dataIndex: 'COLUMN_CLTV' },
    { title: '프리셋', width: 160, variant: 'input', dataIndex: 'COLUMN_PRESET' },
  ],
}

// 선택 행 브리지 — 표마다 따로 둔다(한 표의 선택이 다른 표 확인창에 섞이지 않게).
const selRefs: Record<TableId, Row[]> = { [HEAD]: [], [ITEM]: [], [SUB]: [] }
// 확인창(모듈 함수) 성공 뒤 그 표 선택 비우기 — 컴포넌트가 마운트 동안 setter 를 꽂는다
const selReset: { current: (table: TableId) => void } = { current: () => {} }
const selCtx = (table: TableId): ActionCtx => ({ selected: selRefs[table] })

const ALERT_ICON = <span style={{ display: 'inline-flex', lineHeight: 0, marginInlineEnd: 12 }}><DsIcon name="ic-alertCircle" size={22} color="#faad14" filled /></span>

const confirmSave = (table: TableId) => {
  const count = selRefs[table].length
  core.Modal.confirm({
    icon: ALERT_ICON,
    title: '알림',
    content: `선택한 ${TABLE_NOUN[table]} ${count}건을 저장하시겠어요?`,
    okText: '저장',
    cancelText: '취소',
    onOk: async () => {
      await wiring.actions.save(table, selCtx(table))
      core.message.success(`${count}건 저장 완료`)
      selReset.current(table)
    },
  })
}

const confirmDelete = async (table: TableId) => {
  const count = selRefs[table].length
  let notice: ReactNode
  try {
    notice = await wiring.deleteNotice?.(table, selCtx(table))
  } catch {
    return // 건수를 못 받으면 확인창을 열지 않는다 — 배선이 실패를 알린다
  }
  core.Modal.confirm({
    icon: ALERT_ICON,
    title: '알림',
    content: <>{`선택한 ${TABLE_NOUN[table]} ${count}건을 삭제하시겠어요?`}{notice ? <><br />{notice}</> : null}</>,
    okText: '삭제',
    cancelText: '취소',
    okButtonProps: { danger: true },
    onOk: async () => {
      await wiring.actions.remove(table, selCtx(table))
      core.message.success(`${count}건 삭제 완료`)
      selReset.current(table)
    },
  })
}

const filterFieldClass = (key: string): string => `pbo-ff--${key.replace(/[^A-Za-z0-9_-]/g, '_')}`
const chipText = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null)

/** 조회영역 필드 프레임 — 접힘이면 값 칩(값 없으면 숨김), 열림이면 DS FormItem(라벨 위). 콤보관리 뷰와 같다(접힘 칩 클릭 열기는 뺐다) */
function FilterField({ collapsed, label, chip, fieldKey, children }: { collapsed: boolean; label: string; chip?: string | null; fieldKey: string; children?: ReactNode }) {
  if (collapsed) {
    return (
      <div className="pbo-filter-closed" hidden={chip == null}>
        <div className="pbo-filter-chip-label">
          <label>{label}</label>
        </div>
        <div className="pbo-filter-chip-value">{chip}</div>
      </div>
    )
  }
  return (
    <core.FormItem className={`pbo-filter-field ${filterFieldClass(fieldKey)}`} label={label} labelPosition="top" boxedItem={false}>
      {children}
    </core.FormItem>
  )
}

const noBusy = (): boolean => false
/** 선택 0개면 비활성 — 같은 core.TableSelectionScope 안의 표 선택 개수를 읽는다. 요청 중(useBusy)에도 잠긴다. */
function SelGatedButton({ disabled, ...rest }: ComponentProps<typeof core.Button>) {
  const n = core.useTableSelectionCount()
  const busy = (wiring.useBusy ?? noBusy)()
  return <core.Button {...rest} disabled={disabled || (n ?? 0) === 0 || busy} />
}

/** 표 카운트 — '선택 N / 전체' */
function SelStat(props: ComponentProps<typeof core.TableStat>) {
  const n = core.useTableSelectionCount()
  return <core.TableStat {...props} selectedCount={n && n > 0 ? n : undefined} />
}

/** 선택 행 브리지 — 표의 선택 행을 selRefs[table] 로 흘려 보낸다. */
function SelBridge({ table }: { table: TableId }) {
  const rows = core.useTableSelection()?.rows as Row[] | undefined
  useEffect(() => {
    selRefs[table] = rows ?? []
    return () => {
      selRefs[table] = []
    }
  }, [rows, table])
  return null
}

/**
 * 편집한 행을 체크 선택에 더한다(1.0 CommonUtil.onCellValueChanged 의 setSelected(true) 미러).
 * 콤보관리 뷰 EditSelTable 과 같은 일을 하되, 이 화면은 편집이 모두 wiring.actions.rowsChange 를 지나므로 그 자리에서 키를 모은다.
 */
function useEditSelection(rows: Row[]) {
  const [keys, setKeys] = useState<React.Key[]>([])
  const addKeys = useCallback((changed: Row[]) => setKeys((prev) => [...new Set([...prev.map(String), ...changed.map((r) => r.key)])]), [])
  const valid = useMemo(() => {
    const live = new Set(rows.map((r) => r.key))
    return keys.filter((k) => live.has(String(k)))
  }, [keys, rows])
  return { keys: valid, setKeys, addKeys }
}

/** 표 구획 — 액션바(카운트·저장·삭제 · 페이저·목록 검색·추가) + 표. 표마다 하나씩, 선택 스코프도 따로다. */
function GridSection({ table, title, epoch, onPick }: { table: TableId; title: string; epoch: number; onPick?: (row: Row) => void }) {
  const [pager, setPager] = useState({ page: 1, size: SY13_PAGE_SIZE })
  const [listQ, setListQ] = useState('')
  const { rows, loading } = wiring.rows[table]()
  const sel = useEditSelection(rows)
  const columns = permCols(table, COLUMNS[table])
  const layout = wiring.columnLayout?.[table]?.(columns)
  const picked = wiring.picked[table]?.key
  // [목록 검색] 표시 컬럼 전체 부분일치(대소문자 무시). 버튼 칸(배열)은 코드를 이어 붙여 찾는다
  const listed = useMemo(() => {
    const q = listQ.trim().toLowerCase()
    if (!q) return rows
    const idx = COLUMNS[table].map((c) => c.dataIndex).filter((d): d is string => typeof d === 'string')
    return rows.filter((x) => idx.some((di) => textOf(x[di]).toLowerCase().includes(q)))
  }, [listQ, rows, table])
  const page = Math.min(pager.page, Math.max(1, Math.ceil(listed.length / pager.size)))
  const pageRows = listed.slice((page - 1) * pager.size, page * pager.size)
  useEffect(() => {
    setPager((s) => (s.page === 1 ? s : { ...s, page: 1 }))
    setListQ('')
    sel.setKeys([])
    // eslint-disable-next-line react-hooks/exhaustive-deps -- epoch(조회·초기화·위 행 고르기)가 바뀔 때만 되돌린다
  }, [epoch])
  const change = (next: Row[]) => {
    const before = new Map(rows.map((r) => [r.key, r]))
    const edited = next.filter((r) => {
      const prev = before.get(r.key)
      return prev && Object.keys(r).some((k) => !Object.is(prev[k], r[k]))
    })
    if (edited.length) sel.addKeys(edited)
    wiring.actions.rowsChange(table, next)
  }
  const { addKeys } = sel
  useEffect(() => {
    markEdited[table] = addKeys
    return () => {
      delete markEdited[table]
    }
  }, [table, addKeys])
  return (
    <core.TableSelectionScope key={`${table}-${epoch}`}>
      <SelBridge table={table} />
      <core.Flex vertical gap={8} align="stretch" style={{ flex: 1, minWidth: 0 }}>
        <core.Typography.Title level={5} style={{ margin: 0 }}>
          {title}
        </core.Typography.Title>
        <core.Flex style={{ padding: '4px 0px' }} gap={8} justify="space-between" align="center" wrap>
          <core.Space>
            <SelStat total={rows.length} />
            {permCan('SAVE') && <SelGatedButton type="primary" onClick={() => confirmSave(table)}>저장</SelGatedButton>}
            {permCan('DELETE') && <SelGatedButton onClick={() => { confirmDelete(table).catch(() => undefined) }}>삭제</SelGatedButton>}
          </core.Space>
          <core.Space>
            <core.TablePager variant="paged" page={page} pageSize={pager.size} total={listed.length} showSizeChanger={false} onPage={(p, size) => setPager({ page: size !== pager.size ? 1 : p, size })} />
            <core.Input.Search placeholder="목록 검색" allowClear key={epoch} style={{ width: 200 }} defaultValue={listQ} onSearch={(v) => setListQ((v ?? '').trim())} />
            {permAdd(table) && (
              <core.Button icon={<DsIcon name="ic-plus" size={16} color="currentColor" />} disabled={!wiring.canAdd(table)} onClick={() => wiring.actions.add(table)}>
                추가
              </core.Button>
            )}
          </core.Space>
        </core.Flex>
        <core.Table
          columns={columns}
          dataSource={pageRows}
          loading={loading}
          rowKey="key"
          rowSelection={{ type: 'checkbox', columnWidth: 48, fixed: true, selectedRowKeys: sel.keys, onChange: (k) => sel.setKeys(k) }}
          rowClassName={(r) => (picked != null && (r as Row).key === picked ? 'pbo-v2-row-picked' : '')}
          onCellDoubleClick={onPick ? ({ row }) => { if (!(row as Row).__new) onPick(row as Row) } : undefined}
          size="middle"
          scroll={{ x: 'max-content', y: 360 }}
          onRowsChange={(next) => change(next as Row[])}
          columnOrder={layout?.columnOrder}
          onColumnOrderChange={layout?.onColumnOrderChange}
          columnWidths={layout?.columnWidths}
          onColumnWidthsChange={layout?.onColumnWidthsChange}
          pinnedColumns={layout?.pinnedColumns}
          onPinnedColumnsChange={layout?.onPinnedColumnsChange}
          hiddenColumns={layout?.hiddenColumns}
          onHiddenColumnsChange={layout?.onHiddenColumnsChange}
        />
      </core.Flex>
    </core.TableSelectionScope>
  )
}

const FILTER_DEFAULTS: Record<string, unknown> = {}

export default function ScreenManagementView(props: { wiring: Wiring }) {
  wiring = props.wiring // 모듈 홀더 갱신 — 렌더마다 같은 참조
  const header = usePageHeaderSticky()
  const [filters, setFilters] = useState<Record<string, unknown>>(FILTER_DEFAULTS)
  const [collapsed, setCollapsed] = useState(false)
  // 표별 되돌림 신호 — 조회·초기화는 셋 다, 위 행 고르기는 그 아래 표만
  const [epochs, setEpochs] = useState<Record<TableId, number>>({ [HEAD]: 0, [ITEM]: 0, [SUB]: 0 })
  const bump = (tables: readonly TableId[]) => setEpochs((s) => ({ ...s, ...Object.fromEntries(tables.map((t) => [t, s[t] + 1])) }))
  useEffect(() => { document.body.classList.add('pbo-screen-portal'); return () => { document.body.classList.remove('pbo-screen-portal') } }, [])
  useEffect(() => { selReset.current = (t) => bump([t]); return () => { selReset.current = () => {} } }, [])
  const caption = (t: TableId) => (wiring.picked[t] ? ` — ${wiring.picked[t]?.caption}` : '')
  return (
    <core.ConfigProvider theme={pboLogisticsTheme}>
      <div className="pbo-core-screen pbo-screen-shell pbo-fonts">
        <core.Flex vertical gap={16} align="stretch">
          <core.WorkspaceTab style={{ margin: '-24px -24px 0px -24px' }} activeLabel="화면관리" />
          <core.Flex gap={8} justify="space-between" align="center" ref={header.ref} className={`dev-page-header-wrap pin${header.isSticky ? ' sticky' : ''}`} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', height: 'auto', minHeight: 40, top: 'calc(12px - var(--pbo-page-header-scroller-pad, 0px))', ['--pbo-page-header-bleed-top' as string]: '36px' }}>
            <core.Typography.Title level={4} style={{ margin: 0 }}>화면관리</core.Typography.Title>
          </core.Flex>
          <core.Card styles={{ body: { padding: '12px 20px' } }} style={{ border: '1px solid #d9d9d9', borderRadius: 8, minWidth: 0 }}>
            <div className={collapsed ? 'pbo-filter-collapse-closed' : 'pbo-filter-collapse-open'}>
              {collapsed && <span className="pbo-filter-empty-msg">필터가 적용된 항목이 없습니다. 필터를 열어 원하는 조건으로 검색하세요.</span>}
              <core.Flex gap={20} align="flex-start" wrap>
                {/* 조회조건 = 1.0 SY03 MOMS_PROG_INFO FILTER_CD1~3(SPR_NM·MENU_CD·COLUMN_CD). 1.0 라벨 '화주명' 은 코드 일치 조건이라 칼럼 제목과 같은 '화주코드' 로 쓴다 */}
                <FilterField collapsed={collapsed} label="화주코드" chip={chipText(filters.SPR_NM)} fieldKey="sy13Shipper">
                  <core.Input placeholder="화주코드 입력" value={(filters.SPR_NM as string | undefined) ?? ''} onChange={(e) => setFilters((s) => ({ ...s, SPR_NM: e.target.value }))} style={{ width: '100%' }} />
                </FilterField>
                <FilterField collapsed={collapsed} label="메뉴코드" chip={chipText(filters.MENU_CD)} fieldKey="sy13MenuCode">
                  <core.Input placeholder="메뉴코드 입력" value={(filters.MENU_CD as string | undefined) ?? ''} onChange={(e) => setFilters((s) => ({ ...s, MENU_CD: e.target.value }))} style={{ width: '100%' }} />
                </FilterField>
                <FilterField collapsed={collapsed} label="칼럼코드" chip={chipText(filters.COLUMN_CD)} fieldKey="sy13ColumnCode">
                  <core.Input placeholder="칼럼코드로 메뉴 찾기" value={(filters.COLUMN_CD as string | undefined) ?? ''} onChange={(e) => setFilters((s) => ({ ...s, COLUMN_CD: e.target.value }))} style={{ width: '100%' }} />
                </FilterField>
              </core.Flex>
              <core.Flex gap={8} justify="flex-end" align="center">
                <core.Button icon={collapsed ? <DownOutlined /> : <UpOutlined />} onClick={() => setCollapsed((c) => !c)}>{`필터 ${collapsed ? '열기' : '닫기'}`}</core.Button>
                <span style={{ height: 18, borderLeft: '1px solid #d9d9d9', display: 'block', flex: 'none' }} />
                {permCan('CLEAR') && <core.Button onClick={() => { setFilters(FILTER_DEFAULTS); bump(TABLE_IDS); wiring.onReset?.() }}>초기화</core.Button>}
                {permCan('SEARCH') && <core.Button type="primary" onClick={() => { Promise.resolve(wiring.actions.search(filters)).then((r) => { if (r !== false) bump(TABLE_IDS) }, () => undefined) }}>조회</core.Button>}
              </core.Flex>
            </div>
          </core.Card>
          <GridSection table={HEAD} title="메뉴" epoch={epochs[HEAD]} onPick={(row) => { bump([ITEM, SUB]); wiring.actions.pick(HEAD, row) }} />
          <core.Flex gap={16} align="flex-start">
            <GridSection table={ITEM} title={`그리드${caption(HEAD)}`} epoch={epochs[ITEM]} onPick={(row) => { bump([SUB]); wiring.actions.pick(ITEM, row) }} />
            <GridSection table={SUB} title={`칼럼${caption(ITEM)}`} epoch={epochs[SUB]} />
          </core.Flex>
        </core.Flex>
      </div>
    </core.ConfigProvider>
  )
}
